import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useRevalidator } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import {
  catalogCorporateMessageExampleLinkMsg,
  catalogCorporateMessageExampleMsg,
  catalogCorporateMessageHelpMsg,
  catalogCorporateMessageInstructionMsg,
  catalogCorporateMessageLabelMsg,
  catalogCorporateMessageSelectorMsg,
  closeMsg,
  connectDeviceToRegistrationProgramMsg,
  deviceEnrollmentProgramAliasHelpMsg,
  deviceEnrollmentProgramAliasInfoMsg,
  deviceEnrollmentProgramMsg,
  deviceToEnrollmentProgramMsg,
  deviceToEnrollmentReadMoreMsg,
  enrollmentAliasPlaceHolderMsg,
  fieldCantBeEmptyMsg,
  t,
} from '../../common/i18n/index.js';
import { Checkbox } from '../../common/react-hook-form/components/Checkbox.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { TextInput } from '../../common/react-hook-form/fields/index.js';
import { toBillingAccountsResponseCoCo } from '../../common/utils/billingAccountUtilsCoCo.js';
import { openDialog } from '../../public/site/dialogStore';
import { addNotification } from '../../public/site/notificationStore.js';
import { ExternalLink } from '../ExternalLink/ExternalLink.js';
import { SelectBillingAccountCoCo } from '../PlaceOrder/steps/payment/SelectBillingAccountCoCo.js';
import { SelectableBillingAccountList } from '../SelectableBillingAccountList/SelectableBillingAccountList.js';
import type { BillingAccountCoco } from '../../generated/api/billingAccountCoco.js';
import type { BillingAccountCocoCreationConfig } from '../PlaceOrder/steps/payment/SelectBillingAccountCoCo.js';
import type { ReactNode } from 'react';

export const SectionDisclaimerCoCo = ({ children }: { children: ReactNode }) => (
  <CL.Disclaimer
    className={dsClass.MARGIN_TOP_3}
    disclaimerType="info"
    icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
    showCloseButton
    i18n_disclaimer_closeButtonTitle={t.WOYD(closeMsg)}
    visible
  >
    {children}
  </CL.Disclaimer>
);

export const CorporateMessageFieldsCoCo = () => {
  const { watch } = useFormContext();
  const isCorporateMessageAdded = watch('isCorporateMessageAdded');

  return (
    <>
      <h4>{t.C8DA(catalogCorporateMessageLabelMsg)}</h4>
      <CL.Button
        className={dsClass.MARGIN_BOTTOM_3}
        color="light"
        onClick={() =>
          openDialog({
            body: <>{t.Y0HD(catalogCorporateMessageExampleMsg)}</>,
            header: '',
            type: DialogType.GENERIC_INFO_DIALOG,
          })
        }
        size="s"
      >
        {t.ZU62(catalogCorporateMessageExampleLinkMsg)}
      </CL.Button>
      <Checkbox
        className={dsClass.MARGIN_BOTTOM_3}
        name="isCorporateMessageAdded"
        label={t.FFQZ(catalogCorporateMessageSelectorMsg)}
      />
      {isCorporateMessageAdded && (
        <TextAreaComponent
          rows={5}
          maxLength={1000}
          disabled={false}
          hint={t.HQTV(catalogCorporateMessageHelpMsg)}
          name="catalog.corporateMessage"
          validate={(value: string) => (value ? undefined : t.VPVR(fieldCantBeEmptyMsg))}
        />
      )}
      <SectionDisclaimerCoCo>
        <p className={dsClass.MARGIN_0}>{t.Q57Q(catalogCorporateMessageInstructionMsg)}</p>
      </SectionDisclaimerCoCo>
    </>
  );
};

export const EnrollmentProgramFieldsCoCo = () => {
  const { watch } = useFormContext();
  const enrollmentProgramConsent = watch('catalog.enrollmentProgramConsent');

  return (
    <>
      <h4>{t.Q03S(deviceEnrollmentProgramMsg)}</h4>
      <Checkbox
        className={dsClass.MARGIN_BOTTOM_3}
        name="catalog.enrollmentProgramConsent"
        label={t.MNEQ(connectDeviceToRegistrationProgramMsg)}
      />
      {enrollmentProgramConsent && (
        <TextInput
          label={t.IBVP(enrollmentAliasPlaceHolderMsg)}
          name="catalog.enrollmentProgramAlias"
          required={false}
          placeholder={t.IBVP(enrollmentAliasPlaceHolderMsg)}
          tooltip={t.BJRZ(deviceEnrollmentProgramAliasInfoMsg)}
        />
      )}
      <SectionDisclaimerCoCo>
        <p className={dsClass.MARGIN_0}>
          {t.ZIH3(deviceToEnrollmentProgramMsg)}{' '}
          <ExternalLink href={paths.DEVICE_ENROLLMENT}>{t.X2UI(deviceToEnrollmentReadMoreMsg)}</ExternalLink>
        </p>
        <p className={`${dsClass.MARGIN_TOP_2} ${dsClass.MARGIN_BOTTOM_0}`}>
          {t.CSOC(deviceEnrollmentProgramAliasHelpMsg)}
        </p>
      </SectionDisclaimerCoCo>
    </>
  );
};

export interface DefaultBillingAccountFieldsProps {
  heading: string;
  fieldPrefix: string;
  mdmId: string;
  billingAccounts: BillingAccountCoco[];
  // Enables creating a new billing account with the same form and logic as in the checkout
  createBillingAccount?: BillingAccountCocoCreationConfig;
}

export const DefaultBillingAccountFieldsCoCo = ({
  heading,
  fieldPrefix,
  mdmId,
  billingAccounts,
  createBillingAccount,
}: DefaultBillingAccountFieldsProps) => {
  const { control } = useFormContext();
  const { revalidate } = useRevalidator();
  const { field, fieldState, formState } = useController({
    name: `${fieldPrefix}.billingAccountId`,
    control,
    rules: { required: t.GMD4('The billing account must be chosen') },
  });

  return (
    <>
      <h3 className={dsClass.MARGIN_TOP_0}>{heading}</h3>
      <h4>{t.Y93H('Select default invoicing agreement for device list')}</h4>
      <p>
        {t.SOJT(
          'The selected billing account will be used by default in all orders from this device list. If necessary, the order acceptor can select another billing account instead.'
        )}
      </p>
      <div className={dsClass.MARGIN_BOTTOM_3}>
        <SelectBillingAccountCoCo
          mdmId={mdmId}
          billingAccounts={billingAccounts}
          billingAccountId={field.value}
          onChange={billingAccount => field.onChange(billingAccount.id)}
          error={formState.isSubmitted ? fieldState.error?.message : undefined}
          createBillingAccount={createBillingAccount}
          // The loaded billing accounts are refreshed, so that the created one is also shown in the summary
          onBillingAccountCreated={revalidate}
          onBillingAccountError={message => addNotification(message, 'error')}
        />
      </div>
    </>
  );
};

export interface BillingAccountAdditionalSettingsProps {
  fieldPrefix: string;
  billingAccounts: BillingAccountCoco[];
  defaultSelectedBillingAccountIds?: string[];
}

export const BillingAccountAdditionalSettingsCoCo = ({
  fieldPrefix,
  billingAccounts,
  defaultSelectedBillingAccountIds,
}: BillingAccountAdditionalSettingsProps) => {
  const { setValue, watch } = useFormContext();
  const defaultCatalogBillingAccountId = watch(`${fieldPrefix}.billingAccountId`);
  const [showMultipleBillingAccounts, setShowMultipleBillingAccounts] = useState(
    Boolean(defaultSelectedBillingAccountIds?.length)
  );

  const handleBillingAccountIdsChange = (selectedIds: string[]) => {
    const selected = selectedIds.filter(id => id !== defaultCatalogBillingAccountId);
    setValue(`${fieldPrefix}.selectedBillingAccountIds`, selected);
  };

  return (
    <>
      <h4>{t.LIB5('Billing account additional settings')}</h4>
      <div className="catalog-configuration__bordered-section">
        <Checkbox
          className={dsClass.MARGIN_BOTTOM_2}
          name={`${fieldPrefix}.approverMustSelectBillingAccount`}
          label={t.RXBO('Billing account is selected separately for each order')}
        />
        <div className="catalog-configuration__bordered-section-note">
          {t.VL1R('The order acceptor must select the billing account each time that they accept an order.')}
        </div>
      </div>
      <div className="catalog-configuration__bordered-section">
        <div>
          <CL.Checkbox
            id={`${fieldPrefix}-select-restricted-billing-account-list`}
            className={dsClass.MARGIN_BOTTOM_2}
            checked={showMultipleBillingAccounts}
            onChange={() => {
              if (!showMultipleBillingAccounts) {
                setValue(`${fieldPrefix}.selectedBillingAccountIds`, []);
              }
              setShowMultipleBillingAccounts(!showMultipleBillingAccounts);
            }}
          >
            {t.RP0S('Restricted list of billing accounts')}
          </CL.Checkbox>
        </div>
        <div className="catalog-configuration__bordered-section-note">
          {t.CYUU('Select which billing accounts the order acceptor can choose for orders.')}
        </div>
        {showMultipleBillingAccounts && (
          <div className={classNames(dsClass.MARGIN_TOP_3, dsClass.PADDING_BOTTOM_3)}>
            <SelectableBillingAccountList
              billingAccounts={toBillingAccountsResponseCoCo(billingAccounts)}
              defaultBillingAccountId={defaultCatalogBillingAccountId}
              defaultSelectedBillingAccountIds={defaultSelectedBillingAccountIds}
              handleBillingAccountIdsChange={handleBillingAccountIdsChange}
            />
          </div>
        )}
      </div>
    </>
  );
};
